import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { format, isValid, parseISO } from 'date-fns';
import { Mail, Send, X, CheckCircle, AlertCircle, BarChart3 } from 'lucide-react';
import { getWeeklyReport, emailWeeklyReport } from '../api/reports';
import { WeeklyReport, WeeklyReportRow, WeeklyReportEmailResponse } from '../types/report';
import { useAuth } from '../contexts/AuthContext';
import DataTable, { TableColumn } from '../components/DataTable';
import WeekSelector, { toWeekStart } from '../components/WeekSelector';
import Card from '../components/Card';
import Button from '../components/Button';
import { CardSkeleton } from '../components/LoadingSkeleton';

const formatRange = (start: string, end: string): string =>
  `${format(parseISO(start), 'MMM d')} – ${format(parseISO(end), 'MMM d, yyyy')}`;

const hours = (value: number): string => `${value.toFixed(2)}h`;

const variance = (row: WeeklyReportRow): number =>
  row.current_week_hours - row.current_week_planned_hours;

/** `?week=` is any date in the wanted week; anything unparseable falls back to the current week. */
const weekFromParam = (param: string | null): string => {
  const parsed = param ? parseISO(param) : null;
  return parsed && isValid(parsed) ? toWeekStart(parsed) : toWeekStart(new Date());
};

const columns: TableColumn<WeeklyReportRow>[] = [
  {
    key: 'user_name',
    label: 'User',
    sortable: true,
    render: (row) => (
      <span className="font-medium text-gray-900 dark:text-gray-100">{row.user_name}</span>
    ),
  },
  {
    key: 'name',
    label: 'Project / Category',
    sortable: true,
    render: (row) => (
      <span className="text-gray-700 dark:text-gray-300">
        {row.name}
        {row.item_type === 'non_productive' && (
          <span className="ml-2 text-xs text-gray-400">(non-productive)</span>
        )}
      </span>
    ),
  },
  {
    key: 'current_week_planned_hours',
    label: 'Planned',
    sortable: true,
    className: 'text-right',
    render: (row) => (
      <span className="text-gray-700 dark:text-gray-300">{hours(row.current_week_planned_hours)}</span>
    ),
  },
  {
    key: 'current_week_hours',
    label: 'Actual',
    sortable: true,
    className: 'text-right',
    render: (row) => <span className="text-gray-700 dark:text-gray-300">{hours(row.current_week_hours)}</span>,
  },
  {
    key: 'variance',
    label: 'Variance',
    sortable: true,
    className: 'text-right',
    sortValue: variance, // computed here, not returned by the API, so DataTable needs the accessor
    render: (row) => {
      const value = variance(row);
      // Over-plan and under-plan read differently; exactly on plan stays neutral.
      const tone =
        value > 0
          ? 'text-amber-600 dark:text-amber-400'
          : value < 0
            ? 'text-red-600 dark:text-red-400'
            : 'text-gray-500 dark:text-gray-400';
      return <span className={tone}>{`${value > 0 ? '+' : ''}${value.toFixed(2)}h`}</span>;
    },
  },
  {
    key: 'next_week_planned_hours',
    label: 'Next Week (Planned)',
    sortable: true,
    className: 'text-right',
    render: (row) => <span className="text-gray-700 dark:text-gray-300">{hours(row.next_week_planned_hours)}</span>,
  },
];

const WeeklyReportPage: React.FC = () => {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [report, setReport] = useState<WeeklyReport | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [emailOpen, setEmailOpen] = useState<boolean>(false);

  const weekParam = searchParams.get('week');
  const selectedWeek = useMemo(() => weekFromParam(weekParam), [weekParam]);
  const isCurrentWeek = selectedWeek === toWeekStart(new Date());
  // No parameter means the current week, matching the API default.
  const requestedWeek = weekParam ? selectedWeek : undefined;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const loaded = await getWeeklyReport(requestedWeek);
        if (!cancelled) setReport(loaded);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Failed to load report');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [requestedWeek]);

  const handleWeekChange = (week: string) => {
    setSearchParams(week === toWeekStart(new Date()) ? {} : { week });
  };

  if (loading && !report) {
    return (
      <div className="max-w-[1200px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-8">
        <div className="h-10 w-48 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse mb-8" />
        <CardSkeleton />
      </div>
    );
  }

  if (error && !report) {
    return (
      <div className="max-w-[1200px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-8">
        <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300">
          {error}
        </div>
      </div>
    );
  }

  if (!report) return null;

  return (
    <div className="max-w-[1200px] mx-auto px-3 sm:px-4 md:px-6 lg:px-8 py-8">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary-600 via-secondary-600 to-accent-600 bg-clip-text text-transparent flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-primary-600" />
            Weekly Report
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            {isCurrentWeek ? 'This week' : 'Selected week'} (actuals):{' '}
            <strong>{formatRange(report.current_week_start, report.current_week_end)}</strong>
            {' · '}
            {isCurrentWeek ? 'Next week' : 'Following week'} (planned):{' '}
            <strong>{formatRange(report.next_week_start, report.next_week_end)}</strong>
          </p>
        </div>
        <Button variant="primary" leftIcon={Mail} onClick={() => setEmailOpen(true)}>
          Email report
        </Button>
      </div>

      <WeekSelector weekStartDate={selectedWeek} onWeekChange={handleWeekChange} className="mb-6" />

      {error && (
        <div className="mb-6 p-4 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-300">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card className="p-5">
          <p className="text-sm text-gray-500 dark:text-gray-400">Planned</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{hours(report.current_week_planned_total)}</p>
        </Card>
        <Card className="p-5">
          <p className="text-sm text-gray-500 dark:text-gray-400">Logged</p>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{hours(report.current_week_total)}</p>
        </Card>
        <Card className="p-5">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {isCurrentWeek ? 'Next week planned' : 'Following week planned'}
          </p>
          <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{hours(report.next_week_planned_total)}</p>
        </Card>
      </div>

      <Card className="p-4">
        <div className="max-h-[60vh] overflow-y-auto">
          <DataTable
            columns={columns}
            data={report.rows}
            getRowKey={(row) => `${row.user_id}-${row.item_type}-${row.item_id}`}
            defaultSortKey="user_name"
            defaultSortDirection="asc"
            emptyMessage="Nothing planned or logged for the weeks shown."
          />
        </div>
      </Card>

      {emailOpen && (
        <EmailReportModal
          defaultRecipient={user?.email || ''}
          weekStart={selectedWeek}
          weekLabel={formatRange(report.current_week_start, report.current_week_end)}
          onClose={() => setEmailOpen(false)}
        />
      )}
    </div>
  );
};

interface EmailReportModalProps {
  defaultRecipient: string;
  weekStart: string;
  weekLabel: string;
  onClose: () => void;
}

const EmailReportModal: React.FC<EmailReportModalProps> = ({
  defaultRecipient,
  weekStart,
  weekLabel,
  onClose,
}) => {
  const [recipient, setRecipient] = useState<string>(defaultRecipient);
  const [sending, setSending] = useState<boolean>(false);
  const [result, setResult] = useState<WeeklyReportEmailResponse | null>(null);

  const handleSend = async () => {
    if (!recipient || !recipient.includes('@')) {
      setResult({ success: false, message: 'Please enter a valid email address' });
      return;
    }
    setSending(true);
    setResult(null);
    try {
      const response = await emailWeeklyReport({ recipient_email: recipient, week_start: weekStart });
      setResult(response);
      if (response.success) {
        setTimeout(onClose, 2500);
      }
    } catch (err) {
      setResult({ success: false, message: err instanceof Error ? err.message : 'Failed to send report' });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <Send className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Email Weekly Report</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <div>
            <label htmlFor="report-recipient" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Recipient Email <span className="text-red-500">*</span>
            </label>
            <input
              id="report-recipient"
              type="email"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder="manager@example.com"
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              disabled={sending}
            />
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              Sends the all-users report for <strong>{weekLabel}</strong> via Azure Communication Services.
            </p>
          </div>

          {result && (
            <div
              className={`p-4 rounded-lg flex items-start gap-3 ${
                result.success
                  ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                  : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
              }`}
            >
              {result.success ? (
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
              ) : (
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              )}
              <p
                className={`font-medium ${
                  result.success ? 'text-green-800 dark:text-green-300' : 'text-red-800 dark:text-red-300'
                }`}
              >
                {result.message}
              </p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
          <Button variant="outline" onClick={onClose} disabled={sending}>
            {result?.success ? 'Close' : 'Cancel'}
          </Button>
          <Button variant="primary" leftIcon={Send} onClick={handleSend} disabled={sending || !recipient} isLoading={sending}>
            {sending ? 'Sending...' : 'Send Report'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default WeeklyReportPage;
